<template>
  <div class="m-0" v-if="isDataGeneratorRequest">
    <v-dialog fullscreen v-model="displayPlotDialog">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="displayPlotDialog = false">
            <span class="v-btn__content"
              ><i
                aria-hidden="true"
                class="v-icon notranslate material-icons theme--light"
                >close</i
              ></span
            >
          </v-btn>
          <v-toolbar-title>{{ displayedPlotTitle }} </v-toolbar-title>
        </v-toolbar>
        <div class="dialogContentContainer">
          <div id="fullScreenPlotDiv"></div>
        </div>
      </v-card>
    </v-dialog>
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation></exo-mission-computation>
    </div>
    <v-dialog v-if="displayError" v-model="dialog">
      <template v-slot:activator="{ on: dialog }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on: tooltip }">
            <v-col class="bug-btn-col">
              <v-btn large class="bug-button" v-on="{ ...dialog, ...tooltip }">
                <icon class="red--text" scale="1.5" name="user-injured" />
                <span class="red--text bug-btn-txt">Bug report</span>
              </v-btn>
            </v-col>
          </template>
          <span>Display bug report</span>
        </v-tooltip>
      </template>
      <error-log-display
        :root="_self"
        :computationLog="currentResultsLog"
      ></error-log-display>
    </v-dialog>
    <div v-if="displayResult & !displayError">
      <v-row>
        <v-col class="pb-3">
          <v-row no-gutters>
            <div class="d-inline-flex ml-3 justify-center align-center">
              <span class="headline grey--text">{{
                currentMission.name.toUpperCase()
              }}</span>
            </div>
            <v-spacer></v-spacer>
            <v-bottom-sheet inset v-if="displayWarning">
              <template v-slot:activator="{ on: bottomSheet }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      color="warning"
                      v-on="{ ...tooltip, ...bottomSheet }"
                    >
                      <icon
                        scale="1.5"
                        color="warning"
                        class="orange--text"
                        name="exclamation"
                      ></icon>
                    </v-btn>
                  </template>
                  <span>Alert message</span>
                </v-tooltip>
              </template>
              <v-card>
                <div class="alert-div"></div>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="displayWarningEccentricity"
                >
                  Disclaimer: eccentricity might change during the manoeuvres
                  with system constraints. Please let us know if the final orbit
                  does not match your expectation during a mission. We will
                  investigate and fix it.
                </v-alert>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="displayWarningDutyCycle"
                >
                  Disclaimer: the spacecraft orbital average power was not
                  enough to supply the propulsion system warm-up and thrust. To
                  respect the max mean duty cycle, the orbital duty cycle used
                  in the simulation was lower than the one selected by the user:
                  <br />
                  <i
                    >spacecraft definition duty cycle:
                    {{
                      (
                        100 *
                        this.currentResultsLog.results.maneuveringStrategy
                          .spacecraftDefinitionDutyCycle
                      ).toFixed(2)
                    }}
                    % / orbital duty cycle used in the simulation:
                    {{
                      (
                        100 *
                        this.currentResultsLog.results.maneuveringStrategy
                          .orbitalDutyCycle
                      ).toFixed(2)
                    }}
                    %</i
                  >
                </v-alert>
              </v-card>
            </v-bottom-sheet>
          </v-row>
        </v-col>
      </v-row>

      <v-divider class="mt-0"></v-divider>
      <div
        class="px-3 pt-3"
        v-if="resultsLoaded && nbOfGeneratedMeasurements != null"
      >
        <v-row>
          <v-col>
            <div>
              {{ nbOfGeneratedMeasurements }} ephemeris have been generated.
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-dialog
              v-model="downloadECIDialog"
              :persistent="downloadingECI"
              width="500"
            >
              <template v-slot:activator="{}">
                <v-btn
                  color="primary"
                  class="ml-1 white--text"
                  small
                  outlined
                  @click="downloadECIDialog = true"
                >
                  Export results as .eci
                </v-btn>
              </template>

              <v-card :loading="downloadingECI">
                <v-card-title style="background: #3be287">
                  <span class="headline black--text"
                    >Export data as .eci file</span
                  >
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    small
                    class="m-0"
                    :disabled="downloadingECI"
                    @click="downloadECIDialog = false"
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="mt-5" style="font-size: 100%">
                  <template v-if="!downloadingECI">
                    <v-row>
                      {{ nbOfGeneratedMeasurements }} files will be downloaded.
                      Do you want to continue ?
                    </v-row>
                    <v-row class="mt-5">
                      <input
                        type="checkbox"
                        id="zipCheckbox"
                        :disabled="downloadingECI"
                        v-model="zipChecked"
                      />
                      <label for="zipCheckbox" class="ml-2">
                        Bundle all files as a .zip archive</label
                      >
                    </v-row>
                  </template>
                  <template v-else>
                    <v-row>Creating and downloading files ...</v-row>
                  </template>
                </v-card-text>
                <v-card-actions class="mt-n7">
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    text
                    v-on:click="downloadECI()"
                    :disabled="downloadingECI"
                  >
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
          <v-col>
            <v-dialog
              v-model="saveToDbDialog"
              :persistent="savingToDb"
              width="500"
            >
              <template v-slot:activator="{}">
                <v-btn
                  color="primary"
                  class="ml-1 white--text"
                  small
                  outlined
                  @click="saveToDbDialog = true"
                >
                  Save to database
                </v-btn>
              </template>

              <v-card :loading="savingToDb">
                <v-card-title style="background: #3be287">
                  <span class="headline black--text">Save to database</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    small
                    class="m-0"
                    :disabled="savingToDb"
                    @click="saveToDbDialog = false"
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="mt-5" style="font-size: 100%">
                  <template v-if="!savingToDb">
                    <v-row>
                      {{ nbOfGeneratedMeasurements }} days of data will be saved
                      to database. Do you want to continue ?
                    </v-row>
                  </template>
                  <template v-else>
                    <v-row>Saving to database ...</v-row>
                  </template>
                </v-card-text>
                <v-card-actions class="mt-n7">
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    text
                    v-on:click="saveToDb()"
                    :disabled="savingToDb"
                  >
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>
      </div>
      <v-row v-if="data" class="px-3"
        ><v-col>
          <v-card outlined color="primary">
            <v-card-title class="pa-0 mb-4 mt-3"
              ><icon
                class="ml-6 mr-4"
                color="white"
                scale="1.5"
                name="chart-line"
              />
              <span class="font-weight-light ml-2 text-truncate white--text"
                >Plots
              </span>
            </v-card-title>
            <v-card tile height="100%">
              <v-col class="d-flex justify-space-around">
                <div
                  v-for="graph in graphs"
                  :key="graph.label"
                  :id="graph.divId"
                  style="width: 33%; height: 300px"
                ></div>
              </v-col> </v-card></v-card></v-col
      ></v-row>
    </div>
  </div>
</template>

<script>
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'
import Plotly from 'plotly.js-dist'
import dashboardTools from '../../tools/dashboard'

Vue.use(dashboardComponents)

export default {
  name: 'DataGeneratorDashboard',
  mixins: [errorMixin, plotLayout, plotInput, plotData],
  components: {},
  data() {
    const me = this
    return {
      nbOfGeneratedMeasurements: null,
      graphs: [
        {
          label: 'semi-major axis',
          prop: 'semiMajorAxis',
          divId: 'smaPlotDivDef',
          yType: 'distance',
          tickformat: '.7g',
          yAxisThreshold: 0.00002
        },
        {
          label: 'inclination',
          prop: 'inclination',
          divId: 'incPlotDivDef',
          yType: 'angle',
          tickformat: '.5g',
          yAxisThreshold: 0.5
        },
        {
          label: 'eccentricity',
          prop: 'eccentricity',
          divId: 'eccPlotDivDef',
          yType: 'coefficient',
          yAxisThreshold: 0.001
        }
      ],
      plotConfig: {
        responsive: true,
        modeBarButtonsToRemove: ['select2d', 'lasso2d'],
        modeBarButtonsToAdd: [
          {
            name: 'Full screen',
            icon: Plotly.Icons.pan,
            direction: 'up',
            click: async function (gd) {
              if (!me.displayPlotDialog) {
                const index = me.graphs.findIndex((object) => {
                  return object.divId === gd.id
                })
                me.displayPlotDialog = true
                await me.$nextTick()
                me.displayedPlotTitle = 'Mean ' + me.graphs[index].label
                me.drawGraph(me.graphs[index], true)
              }
            }
          }
        ]
      },
      displayedPlotTitle: '',
      displayPlotDialog: false,
      data: null,
      dialog: false,
      expanded: [],
      detectedManeuverExpanded: [],
      expansion: [0],
      isDrawn: [],
      drawer: false,
      plotingAreaName: 'plotingAreaAnalysis',
      statsCardName: 'statsCard',
      type: 'DataGeneratorResult',
      downloadECIDialog: false,
      downloadingECI: false,
      zipChecked: false,
      saveToDbDialog: false,
      savingToDb: false
    }
  },

  watch: {
    async data() {
      await this.$nextTick()
      if (this.data && !dashboardTools.displayError(this)) {
        this.graphs.forEach((graph) => this.drawGraph(graph))
      }
    },
    async currentResultsLog() {
      if (this.currentResultsLog) {
        this.updateData()
      }
    }
  },

  mounted() {
    this.updateData()
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog'
    }),
    displayComputationWidget() {
      return this.computationNeeded || !this.resultsLoaded
    },
    displayResult() {
      return this.resultsLoaded && !this.computationNeeded
    },
    displayError() {
      return dashboardTools.displayError(this)
    },
    computationNeeded() {
      return (
        this.currentMission &&
        this.currentMission.computationRequested &&
        this.currentMission.computationLogId === ''
      )
    },
    resultsLoaded() {
      return (
        this.currentResultsLog != null && this.currentResultsLog.results != null
      )
    },
    resultAvailable() {
      return (
        this.resultsLoaded &&
        this.currentResultsLog.results ===
          Object(this.currentResultsLog.results)
      ) // check object, not array
    },
    displayWarningEccentricity() {
      return this.currentMission.attitudeAndSystemSimulated
    },
    displayWarningDutyCycle() {
      let maneuveringStrategyResults =
        this.currentResultsLog.results.maneuveringStrategy
      if (maneuveringStrategyResults == null) {
        return false
      } else {
        return (
          maneuveringStrategyResults.spacecraftDefinitionDutyCycle >
          maneuveringStrategyResults.orbitalDutyCycle
        )
      }
    },
    displayWarning() {
      return this.displayWarningEccentricity || this.displayWarningDutyCycle
    },
    computationLogId() {
      return this?.currentResultsLog?.id
    },
    isDataGeneratorRequest() {
      return this?.currentMission?.typeName === 'DataGeneratorRequest'
    },
    basePlotLayout() {
      return {
        plot_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        paper_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        font: { color: this.$vuetify.theme.dark ? 'white' : '#121212' },
        xaxis: {
          title: { text: 'Time (Days)' }
        },
        margin: { t: 0 }
      }
    }
  },

  methods: {
    displayNdigits(valeur, digits) {
      return dataTools.displayNdigits(valeur, digits)
    },

    async updateData() {
      if (this?.currentMission?.computationLogId) {
        this.$http
          .get(
            `users/${this.currentUser.id}/generatedspmeasurements/count/${this.currentMission.computationLogId}`
          )
          .then((response) => {
            this.nbOfGeneratedMeasurements = response.data
          })
          .catch((e) => {
            console.log(e)
          })
      }
      if (this.resultAvailable) {
        const data = {}
        const orbitReports = this.currentResultsLog.results.analyzedMeanOrbits
        data.graphs = this.getGraphDataFromResults(orbitReports)
        this.data = data
      }
    },

    getGraphDataFromResults(orbitReports) {
      const graphsData = {}
      this.graphs.forEach((graph) => {
        const x = orbitReports.map(
          (orbitReport) => orbitReport.orbit.orbitDateTime
        )
        const y = orbitReports.map(
          (orbitReport) => orbitReport.orbit[graph.prop]
        )
        graphsData[graph.label] = { x, y }
      })
      return graphsData
    },

    drawGraph(graph, inFullScreenDialog) {
      const plotDiv = document.getElementById(
        inFullScreenDialog ? 'fullScreenPlotDiv' : graph.divId
      )
      const unit = dataTools.getUnitDisplayed(
        dataTools.getDefaultGuiUnit(graph.yType)
      )
      const plotLayout = {
        ...this.basePlotLayout,
        yaxis: {
          title: {
            text: `Mean ${graph.label} ${unit ? '(' + unit + ')' : ''}`
          },
          tickformat: graph.tickformat ? graph.tickformat : '.3g'
        },
        legend: { orientation: 'h', xanchor: 'center', x: 0.5, y: 1.2 }
      }

      let range = this.determineGraphRange(graph)
      if (range) {
        plotLayout.yaxis.range = range
      }

      Plotly.newPlot(
        plotDiv,
        [
          {
            x: this.data.graphs[graph.label].x,
            y: this.data.graphs[graph.label].y.map((y) =>
              dataTools.convertSi2gui(graph.yType, y)
            ),
            marker: {
              color: this.$vuetify.theme.dark ? 'white' : '#1E1E1E'
            },
            name: '',
            type: 'scatter',
            showlegend: false
          }
        ],
        plotLayout,
        this.plotConfig
      )
    },

    determineGraphRange(graph) {
      let min = null,
        max = null,
        diff = null,
        range = null

      // DETERMINE MIN AND MAX VALUES
      this.data.graphs[graph.label].y.forEach((yval, _) => {
        let val = parseFloat(dataTools.convertSi2gui(graph.yType, yval))
        if (min == null || val < min) {
          min = val
        }
        if (max == null || val > max) {
          max = val
        }
      })

      diff = max - min

      // IF DIFF IS BELOW THRESHOLD THEN INCREASE Y AXIS RANGE BOUNDARIES
      if (diff < graph.yAxisThreshold) {
        let boundaryIncrease = parseFloat(graph.yAxisThreshold - diff) / 2
        min -= boundaryIncrease
        max += boundaryIncrease

        // LIMIT MIN TO ZERO FOR ECC AND INC
        if (graph.prop == 'eccentricity' || graph.prop == 'inclination') {
          if (min < 0) {
            min = 0
            max = graph.yAxisThreshold
          }
        }

        range = [min, max]
      }

      return range
    },

    saveToDb() {
      this.savingToDb = true
      this.$http
        .post(
          `users/${this.currentUser.id}/generatedspmeasurements/dbexport/${this.currentMission.computationLogId}`
        )
        .catch((e) =>
          this.displayMessage(
            'Error while saving to database.',
            e,
            'notification',
            'error'
          )
        )
        .finally(() => {
          this.savingToDb = false
          this.saveToDbDialog = false
        })
    },

    downloadECI() {
      if (this.zipChecked) {
        this.downloadZIP()
      } else {
        this.downloadAllECIFiles()
      }
    },

    downloadZIP() {
      if (this?.currentMission?.computationLogId) {
        const endpoint = `users/${this.currentUser.id}/generatedspmeasurements/tozip/${this.currentMission.computationLogId}`
        this.downloadingECI = true
        this.download(endpoint)
          .catch((e) => {
            this.displayMessage(
              'Error while downloading eci files.',
              e,
              'notification',
              'error'
            )
          })
          .finally(() => {
            this.downloadingECI = false
            this.downloadECIDialog = false
            this.zipChecked = false
          })
      }
    },

    downloadAllECIFiles() {
      const listOfNames = this?.currentResultsLog?.results?.spMeasurementsNames
      if (listOfNames) {
        this.downloadingECI = true
        const requests = []
        for (const name of listOfNames) {
          const endpoint = `users/${this.currentUser.id}/generatedspmeasurements/toeci/${name}`
          requests.push(this.download(endpoint))
        }
        Promise.all(requests)
          .catch((e) => {
            this.displayMessage(
              'Error while downloading eci files.',
              e,
              'notification',
              'error'
            )
          })
          .finally(() => {
            this.downloadingECI = false
            this.downloadECIDialog = false
            this.zipChecked = false
          })
      }
    },

    async download(endPoint, fileName = null) {
      await this.$http
        .get(endPoint, {
          responseType: 'blob'
        })
        .then((response) => {
          const blob = new Blob([response.data], { type: response.data.type })
          let a = window.document.createElement('a')
          a.href = window.URL.createObjectURL(blob)

          const contentDisposition = response.headers['content-disposition']
          const match =
            contentDisposition != null
              ? contentDisposition.match(/filename\s*=\s*"(.+)"/i)
              : null
          let serverFileName = null
          if (match != null && match.length > 0) {
            serverFileName = match[1]
          }
          a.download =
            fileName ||
            serverFileName ||
            `output${this.typeToExtension(response.data.type)}`

          document.body.appendChild(a)
          a.click()
          URL.revokeObjectURL(a.href)
          document.body.removeChild(a)
        })
        .catch((e) => {
          throw e
        })
    },

    typeToExtension(type) {
      switch (type) {
        case 'audio/aac':
          return '.aac'
        case 'application/x-abiword':
          return '.abw'
        case 'application/x-freearc':
          return '.arc'
        case 'image/avif':
          return '.avif'
        case 'video/x-msvideo':
          return '.avi'
        case 'application/vnd.amazon.ebook':
          return '.azw'
        case 'application/octet-stream':
          return '.bin'
        case 'image/bmp':
          return '.bmp'
        case 'application/x-bzip':
          return '.bz'
        case 'application/x-bzip2':
          return '.bz2'
        case 'application/x-cdf':
          return '.cda'
        case 'application/x-csh':
          return '.csh'
        case 'text/css':
          return '.css'
        case 'text/csv':
          return '.csv'
        case 'application/msword':
          return '.doc'
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          return '.docx'
        case 'application/vnd.ms-fontobject':
          return '.eot'
        case 'application/epub+zip':
          return '.epub'
        case 'application/gzip':
          return '.gz'
        case 'image/gif':
          return '.gif'
        case 'text/html':
          return '.html'
        case 'image/vnd.microsoft.icon':
          return '.ico'
        case 'text/calendar':
          return '.ics'
        case 'application/java-archive':
          return '.jar'
        case 'image/jpeg':
          return '.jpg'
        case 'text/javascript':
          return '.js'
        case 'application/json':
          return '.json'
        case 'application/ld+json':
          return '.jsonld'
        case 'audio/midi':
          return '.midi'
        case 'audio/x-midi':
          return '.midi'
        case 'audio/mpeg':
          return '.mp3'
        case 'video/mp4':
          return '.mp4'
        case 'video/mpeg':
          return '.mpeg'
        case 'application/vnd.apple.installer+xml':
          return '.mpkg'
        case 'application/vnd.oasis.opendocument.presentation':
          return '.odp'
        case 'application/vnd.oasis.opendocument.spreadsheet':
          return '.ods'
        case 'application/vnd.oasis.opendocument.text':
          return '.odt'
        case 'audio/ogg':
          return '.oga'
        case 'video/ogg':
          return '.ogv'
        case 'application/ogg':
          return '.ogx'
        case 'audio/opus':
          return '.opus'
        case 'font/otf':
          return '.otf'
        case 'image/png':
          return '.png'
        case 'application/pdf':
          return '.pdf'
        case 'application/x-httpd-php':
          return '.php'
        case 'application/vnd.ms-powerpoint':
          return '.ppt'
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          return '.pptx'
        case 'application/vnd.rar':
          return '.rar'
        case 'application/rtf':
          return '.rtf'
        case 'application/x-sh':
          return '.sh'
        case 'image/svg+xml':
          return '.svg'
        case 'application/x-tar':
          return '.tar'
        case 'image/tiff':
          return '.tif'
        case 'video/mp2t':
          return '.ts'
        case 'font/ttf':
          return '.ttf'
        case 'text/plain':
          return '.txt'
        case 'application/vnd.visio':
          return '.vsd'
        case 'audio/wav':
          return '.wav'
        case 'audio/webm':
          return '.weba'
        case 'video/webm':
          return '.webm'
        case 'image/webp':
          return '.webp'
        case 'font/woff':
          return '.woff'
        case 'font/woff2':
          return '.woff2'
        case 'application/xhtml+xml':
          return '.xhtml'
        case 'application/vnd.ms-excel':
          return '.xls'
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return '.xlsx'
        case 'application/xml':
          return '.xml'
        case 'application/vnd.mozilla.xul+xml':
          return '.xul'
        case 'application/zip':
          return '.zip'
        case 'video/3gpp':
          return '.3gp'
        case 'audio/3gpp':
          return '.3gp'
        case 'video/3gpp2':
          return '.3g2'
        case 'audio/3gpp2':
          return '.3g2'
        case 'application/x-7z-compressed':
          return '.7z'
        default:
          return ''
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');

#fullScreenPlotDiv {
  width: 100%;
  height: 100%;
}

.dialogContentContainer {
  padding: 30px;
  height: calc(100vh - 70px);
}

.bug-btn-txt {
  margin-left: 12px;
}

.bug-btn-col {
  text-align: center;
}
</style>
